import { DocumentCategory, DocumentSubcategory, DocumentType, DocumentField, Document } from '../types/Document';
import { organizeDocumentsByPeriod, isDocumentExpired } from './documentUtils';

export interface CompletionStats {
  totalItems: number;
  completedItems: number;
  missingItems: number; // Keep the typo to match existing API
  expiredItems: number;
  completionRate: number;
}

export interface CategoryCompletionStats extends CompletionStats {
  subcategories: Record<string, CompletionStats>;
}

export interface OverallCompletionData {
  categories: Record<string, CategoryCompletionStats>;
  subcategories: Record<string, CompletionStats>;
  overall: CompletionStats;
}

/**
 * Calculate completion statistics for a single document type
 * This function now properly handles recurring documents by only checking the current period
 */
export const calculateDocumentTypeCompletion = (
  documentType: DocumentType,
  userDocuments: Document[]
): { completed: boolean; expired: boolean; missing: boolean } => {
  const typeDocuments = userDocuments.filter(doc => doc.typeID === documentType.id);
  
  // Use the same logic as the UI to determine current vs historical documents
  const { currentDocument } = organizeDocumentsByPeriod(typeDocuments, documentType);
  
  // Only check the current period document
  if (!currentDocument) {
    return { completed: false, expired: false, missing: true };
  }

  // Check if the current period document is expired
  const expired = isDocumentExpired(currentDocument);

  if (expired) {
    return { completed: false, expired: true, missing: false };
  }

  return { completed: true, expired: false, missing: false };
};

/**
 * Calculate completion statistics for a single document field
 */
export const calculateFieldCompletion = (
  field: DocumentField,
  userFieldValues: Record<string, any>
): { completed: boolean; missing: boolean } => {
  const fieldValue = userFieldValues[field.id];
  const isEmpty = !fieldValue || (typeof fieldValue === 'string' && fieldValue.trim() === '');
  
  return {
    completed: !isEmpty,
    missing: isEmpty
  };
};

/**
 * Calculate completion statistics for a subcategory
 */
export const calculateSubcategoryCompletion = (
  subcategory: DocumentSubcategory,
  documentTypes: DocumentType[],
  documentFields: DocumentField[],
  userDocuments: Document[],
  userFieldValues: Record<string, any>
): CompletionStats => {
  const subcategoryTypes = documentTypes.filter(type => type.subcategory_id === subcategory.id);
  const subcategoryFields = documentFields.filter(field => field.subcategory_id === subcategory.id);
  
  let totalItems = subcategoryTypes.length + subcategoryFields.length;
  let completedItems = 0;
  let missingItems = 0;
  let expiredItems = 0;

  // Skip subcategories with no requirements
  if (totalItems === 0) {
    return {
      totalItems: 0,
      completedItems: 0,
      missingItems: 0,
      expiredItems: 0,
      completionRate: 0
    };
  }

  // Check document types
  for (const docType of subcategoryTypes) {
    const completion = calculateDocumentTypeCompletion(docType, userDocuments);
    
    if (completion.completed) {
      completedItems++;
    } else if (completion.expired) {
      expiredItems++;
    } else if (completion.missing) {
      missingItems++;
    }
  }

  // Check fields
  for (const field of subcategoryFields) {
    const completion = calculateFieldCompletion(field, userFieldValues);
    
    if (completion.completed) {
      completedItems++;
    } else if (completion.missing) {
      missingItems++;
    }
  }

  const completionRate = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

  return {
    totalItems,
    completedItems,
    missingItems,
    expiredItems,
    completionRate
  };
};

/**
 * Calculate completion statistics for a category
 */
export const calculateCategoryCompletion = (
  category: DocumentCategory,
  subcategories: DocumentSubcategory[],
  documentTypes: DocumentType[],
  documentFields: DocumentField[],
  userDocuments: Document[],
  userFieldValues: Record<string, any>
): CategoryCompletionStats => {
  const categorySubcategories = subcategories.filter(sub => sub.category_id === category.id);

  let totalItems = 0;
  let completedItems = 0;
  let missingItems = 0;
  let expiredItems = 0;
  const subcategoryStats: Record<string, CompletionStats> = {};

  // Process subcategories first
  for (const subcategory of categorySubcategories) {
    const subStats = calculateSubcategoryCompletion(
      subcategory,
      documentTypes,
      documentFields,
      userDocuments,
      userFieldValues
    );

    // Only include subcategories that have requirements
    if (subStats.totalItems > 0) {
      subcategoryStats[subcategory.id] = subStats;

      totalItems += subStats.totalItems;
      completedItems += subStats.completedItems;
      missingItems += subStats.missingItems;
      expiredItems += subStats.expiredItems;
    }
  }

  // Include document types and fields that are directly on the category (no subcategory)
  const directTypes = documentTypes.filter(dt => dt.category_id === category.id && !dt.subcategory_id);
  const directFields = documentFields.filter(f => f.category_id === category.id && !f.subcategory_id);

  // Process direct document types
  for (const docType of directTypes) {
    const completion = calculateDocumentTypeCompletion(docType, userDocuments);
    totalItems += 1;
    if (completion.completed) {
      completedItems++;
    } else if (completion.expired) {
      expiredItems++;
    } else if (completion.missing) {
      missingItems++;
    }
  }

  // Process direct fields
  for (const field of directFields) {
    const completion = calculateFieldCompletion(field, userFieldValues);
    totalItems += 1;
    if (completion.completed) {
      completedItems++;
    } else if (completion.missing) {
      missingItems++;
    }
  }

  const completionRate = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

  return {
    totalItems,
    completedItems,
    missingItems,
    expiredItems,
    completionRate,
    subcategories: subcategoryStats
  };
};

/**
 * Calculate overall completion statistics for a user
 */
export const calculateOverallCompletion = (
  categories: DocumentCategory[],
  subcategories: DocumentSubcategory[],
  documentTypes: DocumentType[],
  documentFields: DocumentField[],
  userDocuments: Document[],
  userFieldValues: Record<string, any>
): OverallCompletionData => {
  documentTypes = documentTypes.filter(dt => dt.required);
  documentFields = documentFields.filter(df => df.required);

  const categoryStats: Record<string, CategoryCompletionStats> = {};
  const subcategoryStats: Record<string, CompletionStats> = {};
  
  let overallTotal = 0;
  let overallCompleted = 0;
  let overallMissing = 0;
  let overallExpired = 0;

  for (const category of categories) {
    const categoryCompletion = calculateCategoryCompletion(
      category,
      subcategories,
      documentTypes,
      documentFields,
      userDocuments,
      userFieldValues
    );

    // Only include categories that have requirements
    if (categoryCompletion.totalItems > 0) {
      categoryStats[category.id] = categoryCompletion;
      
      // Add subcategory stats to the overall subcategory stats
      Object.assign(subcategoryStats, categoryCompletion.subcategories);
      
      overallTotal += categoryCompletion.totalItems;
      overallCompleted += categoryCompletion.completedItems;
      overallMissing += categoryCompletion.missingItems;
      overallExpired += categoryCompletion.expiredItems;
    }
  }

  const overallCompletionRate = overallTotal > 0 ? (overallCompleted / overallTotal) * 100 : 0;

  return {
    categories: categoryStats,
    subcategories: subcategoryStats,
    overall: {
      totalItems: overallTotal,
      completedItems: overallCompleted,
      missingItems: overallMissing,
      expiredItems: overallExpired,
      completionRate: overallCompletionRate
    }
  };
};
